import React from "react";
import {
  Box,
  Link,
  SpaceBetween,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  RelatedInvestigation,
  RelatedInvestigationRelationEnum,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationRule } from "./InvestigationRule";
import { InvestigationStatusChip } from "./InvestigationStatusChip";
import { investigationPath } from "./investigationUtils";

interface InvestigationRelatedProps {
  investigation: Investigation;
  /** Hydrated summaries from the detail response; falls back to bare IDs when absent. */
  related?: Array<RelatedInvestigation> | null;
  stage: Stage;
}

const RELATION_LABEL: Record<RelatedInvestigationRelationEnum, string> = {
  parent: "Split from",
  child: "Split into",
  recurrence_of: "Recurrence of",
  linked: "See also",
  merged_from: "Merged in",
  merged_into: "Merged into",
  duplicate_of: "Duplicate of",
};

/** Split edges carry the fields the narrower side adds (`ruleDelta`). */
const isSplit = (relation: RelatedInvestigationRelationEnum): boolean =>
  relation === "child" || relation === "parent";

/**
 * Rebuilds the relation list from the investigation's ID fields when the backend did not send
 * `related` (older deployments): same edges, no titles or rule deltas.
 */
export const relationsFromIds = (
  investigation: Investigation
): Array<RelatedInvestigation> => {
  const rows: Array<RelatedInvestigation> = [];
  if (investigation.parentInvestigationId) {
    rows.push({
      investigationId: investigation.parentInvestigationId,
      relation: "parent",
    });
  }
  (investigation.childInvestigationIds || []).forEach((id) =>
    rows.push({ investigationId: id, relation: "child" })
  );
  if (investigation.recurrenceOfInvestigationId) {
    rows.push({
      investigationId: investigation.recurrenceOfInvestigationId,
      relation: "recurrence_of",
    });
  }
  (investigation.mergedFromInvestigationIds || []).forEach((id) =>
    rows.push({ investigationId: id, relation: "merged_from" })
  );
  if (investigation.mergedIntoInvestigationId) {
    rows.push({
      investigationId: investigation.mergedIntoInvestigationId,
      relation: "merged_into",
    });
  }
  if (investigation.duplicateOfInvestigationId) {
    rows.push({
      investigationId: investigation.duplicateOfInvestigationId,
      relation: "duplicate_of",
    });
  }
  const seen = new Set(rows.map((r) => r.investigationId));
  (investigation.linkedInvestigationIds || [])
    .filter((id) => !seen.has(id))
    .forEach((id) => rows.push({ investigationId: id, relation: "linked" }));
  return rows;
};

type Row = RelatedInvestigation & { stage: Stage };

const COLUMNS: TableProps.ColumnDefinition<Row>[] = [
  {
    id: "case",
    header: "Case",
    isRowHeader: true,
    cell: (row) => (
      <Link href={investigationPath(row.investigationId, row.stage)}>
        {row.title || row.investigationId}
      </Link>
    ),
  },
  {
    id: "connection",
    header: "How it's connected",
    cell: (row) => {
      const delta = row.ruleDelta || [];
      return (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>{RELATION_LABEL[row.relation]}</span>
          {isSplit(row.relation) && delta.length > 0 && (
            <>
              <Box color="text-body-secondary">on</Box>
              <InvestigationRule rule={delta} compact />
            </>
          )}
        </SpaceBetween>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    cell: (row) =>
      row.status ? <InvestigationStatusChip status={row.status} /> : "–",
  },
  {
    id: "items",
    header: "Items",
    cell: (row) =>
      typeof row.populationCount === "number"
        ? row.populationCount.toLocaleString()
        : "–",
  },
];

/**
 * The cases this one is connected to (split from / into, merges, duplicate, recurrence, see also)
 * as a plain table: title, how it is connected (for a split, the fields it was split on), status
 * and size. Opaque ids are only shown when the backend sent no title.
 */
export const InvestigationRelated: React.FC<InvestigationRelatedProps> = ({
  investigation,
  related,
  stage,
}) => {
  const rows = related?.length ? related : relationsFromIds(investigation);
  if (!rows.length) return null;
  return (
    <Table
      variant="container"
      columnDefinitions={COLUMNS}
      items={rows.map((row) => ({ ...row, stage }))}
      trackBy={(row) => `${row.relation}-${row.investigationId}`}
      ariaLabels={{ tableLabel: "Linked cases" }}
    />
  );
};

export default InvestigationRelated;
